import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Search, Package, Check, ChevronDown, X } from 'lucide-react';

/**
 * Селектор товара из справочника. Без свободного ввода.
 * Используется при создании/редактировании лотов с направлением «Закупка».
 *
 * @param value      — название выбранного товара (string) — read-only display
 * @param productId  — текущий product_id (string | null)
 * @param onChange   — ({ name, name_en, id }) => void
 * @param required   — флаг обязательности
 */
export default function ProductSelector({ value, productId, onChange, required = false }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);

  useEffect(() => {
    let active = true;
    api.get('/products').then(({ data }) => { if (active) setItems(data || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  // Закрытие при клике вне.
  useEffect(() => {
    const onDoc = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const s = search.trim().toLowerCase();
  const filtered = !s ? items : items.filter(p =>
    (p.name || '').toLowerCase().includes(s) || (p.name_en || '').toLowerCase().includes(s)
  );

  const pick = (p) => {
    onChange && onChange({ name: p.name, name_en: p.name_en || '', id: p.id });
    setSearch('');
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange && onChange({ name: '', name_en: '', id: null });
  };

  // Невыбранное — placeholder; выбранное — отображаем readonly с галочкой.
  const hasSelection = !!productId && !!value;

  return (
    <div className="space-y-1.5" ref={containerRef}>
      <Label className="text-sm font-medium text-slate-700 flex items-center gap-1.5">
        <Package className="w-3.5 h-3.5 text-[#0046AD]" /> Закупаемый товар
        {required && <span className="text-rose-500">*</span>}
      </Label>
      <div className="relative">
        {/* Trigger-кнопка: показывает текущий выбор и открывает дропдаун */}
        <button type="button" onClick={() => setOpen(o => !o)}
          data-testid="lot-product-trigger"
          className={`w-full flex items-center gap-2 px-3 py-2 border rounded-sm bg-white text-left text-sm
            ${hasSelection ? 'border-emerald-400' : 'border-slate-300'}
            hover:border-[#0046AD] focus:outline-none focus:ring-2 focus:ring-[#0046AD]/30 transition`}>
          {hasSelection && <Check className="w-4 h-4 text-emerald-600 shrink-0" />}
          <span className={`flex-1 truncate ${hasSelection ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>
            {hasSelection ? value : 'Выбрать товар из справочника...'}
          </span>
          {hasSelection && (
            <span role="button" onClick={clear} className="cursor-pointer text-slate-400 hover:text-rose-500 shrink-0"
              data-testid="lot-product-clear">
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <div className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-sm shadow-lg max-h-72 overflow-hidden flex flex-col"
            data-testid="lot-product-dropdown">
            <div className="p-2 border-b border-slate-100 sticky top-0 bg-white">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} autoFocus
                  placeholder="Поиск по названию (RU/EN)..."
                  data-testid="lot-product-search"
                  className="pl-9 border-slate-300 rounded-sm h-8 text-sm" />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <div className="px-3 py-4 text-xs text-slate-400 text-center">
                  Ничего не найдено в справочнике.<br />
                  Обратитесь к супер-админу для добавления товара.
                </div>
              ) : filtered.slice(0, 100).map(p => (
                <button type="button" key={p.id} onClick={() => pick(p)}
                  data-testid={`lot-product-option-${p.id}`}
                  className={`w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0
                    ${productId === p.id ? 'bg-blue-50' : ''}`}>
                  <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                    {productId === p.id && <Check className="w-3.5 h-3.5 text-emerald-600" />}
                    {p.name}
                  </div>
                  {p.name_en && <div className="text-xs text-slate-500 mt-0.5 ml-5">EN: {p.name_en}</div>}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-400">Только выбор из справочника. Чтобы добавить новый товар — свяжитесь с супер-администратором.</p>
    </div>
  );
}
